/**
 * Cupones PRO — panel web admin (no embebido en la app móvil).
 * Misma API RPC que el histórico de cupones en la app (eliminado; este panel es la superficie canónica).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { dbClient } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { downloadCsv } from '@/utils/downloadCsv';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertTriangle,
    Download,
    Loader2,
    Plus,
    Power,
    RefreshCw,
    Sparkles,
    Ticket,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const PERIOD_OPTIONS = [
    { value: '1w', label: '1 semana' },
    { value: '1m', label: '1 mes' },
    { value: '3m', label: '3 meses' },
    { value: '6m', label: '6 meses' },
    { value: '12m', label: '12 meses' },
    { value: 'unlimited', label: 'Ilimitado' },
] as const;

type PeriodValue = (typeof PERIOD_OPTIONS)[number]['value'];

const PERIOD_LABEL: Record<string, string> = PERIOD_OPTIONS.reduce(
    (acc, p) => ({ ...acc, [p.value]: p.label }),
    {},
);

type CodeGenerationMode = 'unique_codes' | 'shared_code';

const CODE_GEN_LABEL: Record<CodeGenerationMode, string> = {
    unique_codes: 'Nominativos (N códigos)',
    shared_code: 'Código único (evento)',
};

interface CampaignRow {
    id: string;
    name: string;
    description: string | null;
    period: string;
    maxRedemptions: number;
    redemptionsCount: number;
    grantedCount: number;
    codesCount: number;
    codeGenerationMode: CodeGenerationMode;
    validFrom: string;
    validUntil: string | null;
    sourceChannel: string | null;
    sourceLabel: string | null;
    utmCampaign: string | null;
    isActive: boolean;
    createdAt: string;
    createdByUsername: string | null;
}

interface CouponCodeRow {
    code: string;
    redeemedBy: string | null;
    redeemedUsername: string | null;
    redeemedEmail: string | null;
    redeemedAt: string | null;
}

interface RedemptionRow {
    id: string;
    username: string | null;
    email: string | null;
    codeText: string;
    redeemedAt: string | null;
    grantedUntil: string | null;
}

function normalizeCampaigns(data: unknown): CampaignRow[] {
    if (!data) return [];
    const arr = Array.isArray(data) ? data : [data];
    return arr.map((row: Record<string, unknown>) => ({
        id: String(row.id),
        name: String(row.name),
        description: row.description != null ? String(row.description) : null,
        period: String(row.period),
        maxRedemptions: Number(row.max_redemptions ?? 0),
        redemptionsCount: Number(row.redemptions_count ?? 0),
        grantedCount: Number(row.granted_count ?? 0),
        validFrom: String(row.valid_from),
        validUntil: row.valid_until != null ? String(row.valid_until) : null,
        sourceChannel: row.source_channel != null ? String(row.source_channel) : null,
        sourceLabel: row.source_label != null ? String(row.source_label) : null,
        utmCampaign: row.utm_campaign != null ? String(row.utm_campaign) : null,
        isActive: Boolean(row.is_active),
        createdAt: String(row.created_at),
        createdByUsername:
            row.created_by_username != null ? String(row.created_by_username) : null,
        codesCount: Number(row.codes_count ?? 0),
        codeGenerationMode: (row.code_generation_mode === 'shared_code'
            ? 'shared_code'
            : 'unique_codes') as CodeGenerationMode,
    }));
}

function normalizeCodes(data: unknown): CouponCodeRow[] {
    if (!data) return [];
    const arr = Array.isArray(data) ? data : [data];
    return arr.map((row: Record<string, unknown>) => ({
        code: String(row.code),
        redeemedBy: row.redeemed_by != null ? String(row.redeemed_by) : null,
        redeemedUsername:
            row.redeemed_username != null ? String(row.redeemed_username) : null,
        redeemedEmail: row.redeemed_email != null ? String(row.redeemed_email) : null,
        redeemedAt: row.redeemed_at != null ? String(row.redeemed_at) : null,
    }));
}

function normalizeRedemptions(data: unknown): RedemptionRow[] {
    if (!data) return [];
    const arr = Array.isArray(data) ? data : [data];
    return arr.map((row: Record<string, unknown>) => ({
        id: String(row.id),
        username: row.username != null ? String(row.username) : null,
        email: row.email != null ? String(row.email) : null,
        codeText: String(row.code_text ?? ''),
        redeemedAt: row.redeemed_at != null ? String(row.redeemed_at) : null,
        grantedUntil: row.granted_until != null ? String(row.granted_until) : null,
    }));
}

function StatCard({
    icon,
    iconClass,
    label,
    value,
    sub,
}: {
    icon: ReactNode;
    iconClass: string;
    label: string;
    value: string;
    sub?: string;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4">
            <div className={cn('p-2.5 rounded-lg', iconClass)}>{icon}</div>
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                {sub && <p className="text-[10px] text-muted-foreground/60">{sub}</p>}
            </div>
        </div>
    );
}

export default function ProCouponsPage() {
    const queryClient = useQueryClient();
    const [createOpen, setCreateOpen] = useState(false);
    const [detailCampaign, setDetailCampaign] = useState<CampaignRow | null>(null);
    const [deactivateTarget, setDeactivateTarget] = useState<CampaignRow | null>(null);
    const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

    const {
        data: campaigns,
        isLoading,
        error,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['admin-panel', 'pro-coupons', 'campaigns'],
        queryFn: async () => {
            const { data, error: rpcErr } = await dbClient.rpc('admin_list_coupon_campaigns');
            if (rpcErr) throw new Error(rpcErr.message);
            return normalizeCampaigns(data);
        },
        staleTime: 30_000,
    });

    const stats = useMemo(() => {
        const list = campaigns ?? [];
        return {
            total: list.length,
            active: list.filter(c => c.isActive).length,
            codesRows: list.reduce((acc, c) => acc + c.codesCount, 0),
            capacity: list.reduce((acc, c) => acc + c.maxRedemptions, 0),
            granted: list.reduce((acc, c) => acc + c.grantedCount, 0),
        };
    }, [campaigns]);

    const deactivateMutation = useMutation({
        mutationFn: async (campaignId: string) => {
            const { data, error: rpcErr } = await dbClient.rpc('admin_deactivate_coupon_campaign', {
                p_campaign_id: campaignId,
            });
            if (rpcErr) throw new Error(rpcErr.message);
            const row = Array.isArray(data) ? data[0] : data;
            return row as { success?: boolean; message?: string };
        },
        onSuccess: row => {
            queryClient.invalidateQueries({ queryKey: ['admin-panel', 'pro-coupons', 'campaigns'] });
            setFeedback({
                type: row?.success ? 'ok' : 'err',
                text: row?.message ?? 'Operación realizada',
            });
        },
        onError: (e: Error) => setFeedback({ type: 'err', text: e.message }),
    });

    if (error) {
        return (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-center text-destructive">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                Error cargando campañas: {(error as Error).message}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Cupones PRO"
                description="Captación: códigos nominativos (uno por usuario) o un único código para eventos con cupo máximo. Solo usuarios que aún no son PRO."
            >
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
                    Actualizar
                </button>
                <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="w-4 h-4" />
                    Nueva campaña
                </Button>
            </PageHeader>

            {feedback && (
                <div
                    className={cn(
                        'rounded-xl px-4 py-3 text-sm border',
                        feedback.type === 'ok'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                            : 'bg-destructive/10 text-destructive border-destructive/25',
                    )}
                >
                    {feedback.text}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={<Ticket size={20} />}
                    iconClass="bg-primary/10 text-primary"
                    label="Campañas"
                    value={String(stats.total)}
                />
                <StatCard
                    icon={<Sparkles size={20} />}
                    iconClass="bg-emerald-500/10 text-emerald-500"
                    label="Activas"
                    value={String(stats.active)}
                />
                <StatCard
                    icon={<Ticket size={20} />}
                    iconClass="bg-ocean-400/10 text-ocean-400"
                    label="Filas de código"
                    value={String(stats.codesRows)}
                    sub={`Capacidad total ${stats.capacity} canjes`}
                />
                <StatCard
                    icon={<Download size={20} />}
                    iconClass="bg-amber-400/10 text-amber-400"
                    label="Canjeados"
                    value={String(stats.granted)}
                />
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-card">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Campaña
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Período
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Canjeados
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Canal
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Caducidad
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center text-muted-foreground">
                                        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                                        Cargando campañas…
                                    </td>
                                </tr>
                            ) : (campaigns ?? []).length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center text-muted-foreground">
                                        Aún no hay campañas. Pulsa «Nueva campaña».
                                    </td>
                                </tr>
                            ) : (
                                (campaigns ?? []).map(c => (
                                    <tr key={c.id} className="hover:bg-muted/10">
                                        <td className="px-4 py-3 align-top">
                                            <p className="font-medium text-foreground">{c.name}</p>
                                            {c.description && (
                                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                                    {c.description}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <span
                                                className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium border border-border bg-muted/30 max-w-[11rem] leading-tight"
                                                title={CODE_GEN_LABEL[c.codeGenerationMode]}
                                            >
                                                {CODE_GEN_LABEL[c.codeGenerationMode]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium border border-border bg-muted/40">
                                                {PERIOD_LABEL[c.period] ?? c.period}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-primary">
                                                    {c.grantedCount}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    / {c.maxRedemptions}
                                                </span>
                                            </div>
                                            <div className="w-24 h-1.5 mt-1 rounded-full bg-muted overflow-hidden">
                                                <div
                                                    className="h-full bg-primary"
                                                    style={{
                                                        width: `${Math.min(
                                                            100,
                                                            (c.grantedCount /
                                                                Math.max(c.maxRedemptions, 1)) *
                                                                100,
                                                        )}%`,
                                                    }}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <p className="text-sm">{c.sourceChannel ?? '—'}</p>
                                            {c.sourceLabel && (
                                                <p className="text-xs text-muted-foreground">
                                                    {c.sourceLabel}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            {c.validUntil ? (
                                                <span className="text-sm">
                                                    {format(new Date(c.validUntil), 'dd MMM yyyy', {
                                                        locale: es,
                                                    })}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">
                                                    Sin caducidad
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            {c.isActive ? (
                                                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-400">
                                                    Activa
                                                </span>
                                            ) : (
                                                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                                                    Desactivada
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right align-top">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    title="Ver códigos"
                                                    onClick={() => setDetailCampaign(c)}
                                                >
                                                    <Sparkles className="w-4 h-4" />
                                                </Button>
                                                {c.isActive && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Desactivar"
                                                        onClick={() => setDeactivateTarget(c)}
                                                        disabled={deactivateMutation.isPending}
                                                    >
                                                        <Power className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <CreateCampaignDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={msg => {
                    setFeedback({ type: 'ok', text: msg });
                    queryClient.invalidateQueries({
                        queryKey: ['admin-panel', 'pro-coupons', 'campaigns'],
                    });
                }}
                onError={msg => setFeedback({ type: 'err', text: msg })}
            />

            <CampaignDetailDialog
                campaign={detailCampaign}
                onClose={() => setDetailCampaign(null)}
            />

            <Dialog
                open={!!deactivateTarget}
                onOpenChange={o => {
                    if (!o) setDeactivateTarget(null);
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Desactivar campaña</DialogTitle>
                        <p className="text-sm text-muted-foreground">
                            La campaña <strong className="text-foreground">{deactivateTarget?.name}</strong>{' '}
                            dejará de aceptar canjes. Los usuarios que ya canjearon mantienen su PRO.
                        </p>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setDeactivateTarget(null)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (deactivateTarget) {
                                    deactivateMutation.mutate(deactivateTarget.id);
                                    setDeactivateTarget(null);
                                }
                            }}
                        >
                            Desactivar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CreateCampaignDialog({
    open,
    onOpenChange,
    onCreated,
    onError,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: (msg: string) => void;
    onError: (msg: string) => void;
}) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [period, setPeriod] = useState<PeriodValue>('1m');
    const [maxRedemptions, setMaxRedemptions] = useState(50);
    const [codeGenMode, setCodeGenMode] = useState<CodeGenerationMode>('unique_codes');
    const [sharedCode, setSharedCode] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [sourceChannel, setSourceChannel] = useState('');
    const [sourceLabel, setSourceLabel] = useState('');
    const [utmCampaign, setUtmCampaign] = useState('');
    const [notes, setNotes] = useState('');

    const reset = () => {
        setName('');
        setDescription('');
        setPeriod('1m');
        setMaxRedemptions(50);
        setCodeGenMode('unique_codes');
        setSharedCode('');
        setValidUntil('');
        setSourceChannel('');
        setSourceLabel('');
        setUtmCampaign('');
        setNotes('');
    };

    const createMutation = useMutation({
        mutationFn: async () => {
            const { data, error: rpcErr } = await dbClient.rpc('admin_create_coupon_campaign', {
                p_name: name,
                p_period: period,
                p_max_redemptions: maxRedemptions,
                p_description: description || null,
                p_valid_until: validUntil ? new Date(validUntil).toISOString() : null,
                p_source_channel: sourceChannel || null,
                p_source_label: sourceLabel || null,
                p_utm_campaign: utmCampaign || null,
                p_notes: notes || null,
                p_code_generation_mode: codeGenMode,
                p_shared_code: codeGenMode === 'shared_code' && sharedCode.trim() ? sharedCode.trim() : null,
            });
            if (rpcErr) throw new Error(rpcErr.message);
            const row = Array.isArray(data) ? data[0] : data;
            return row as {
                success?: boolean;
                message?: string;
                codes_generated?: number;
            };
        },
        onSuccess: row => {
            if (row?.success) {
                onCreated(row.message ?? `Campaña creada (${row.codes_generated ?? 0} filas de código).`);
                reset();
                onOpenChange(false);
            } else {
                onError(row?.message ?? 'No se pudo crear la campaña');
            }
        },
        onError: (e: Error) => onError(e.message),
    });

    const valid =
        name.trim().length > 0 &&
        maxRedemptions >= 1 &&
        maxRedemptions <= 10000 &&
        (codeGenMode === 'unique_codes' ||
            sharedCode.trim().length === 0 ||
            (sharedCode.trim().length >= 4 && sharedCode.trim().length <= 40));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Nueva campaña de cupones PRO</DialogTitle>
                    <p className="text-sm text-muted-foreground text-left">
                        Elige si generas un código distinto por cupón (nominativo) o un solo código para el público con cupo
                        máximo de canjes (ideal para eventos). Código automático nominativo: formato{' '}
                        <code className="text-xs bg-muted px-1 rounded">AIPC-XXXX-XXXX</code>.
                    </p>
                </DialogHeader>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="cc-gen-mode">Forma de generación *</Label>
                        <select
                            id="cc-gen-mode"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            value={codeGenMode}
                            onChange={e => setCodeGenMode(e.target.value as CodeGenerationMode)}
                        >
                            <option value="unique_codes">{CODE_GEN_LABEL.unique_codes}</option>
                            <option value="shared_code">{CODE_GEN_LABEL.shared_code}</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                            {codeGenMode === 'unique_codes'
                                ? 'Se crearán tantos códigos aleatorios como indiques; cada uno solo sirve una vez.'
                                : 'Un único código (el que elijas o uno aleatorio). Cuando N usuarios lo canjeen, el N+1 verá cupón agotado.'}
                        </p>
                    </div>
                    {codeGenMode === 'shared_code' && (
                        <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="cc-shared-code">Código del evento (opcional)</Label>
                            <Input
                                id="cc-shared-code"
                                value={sharedCode}
                                onChange={e => setSharedCode(e.target.value.toUpperCase())}
                                placeholder="Vacío = aleatorio estilo AIPC-… · Ej: FERIA-VIGO-26"
                                autoComplete="off"
                            />
                            <p className="text-xs text-muted-foreground">
                                4–40 caracteres: letras, números y guiones. Se normaliza en mayúsculas. Debe ser único en toda
                                la plataforma.
                            </p>
                        </div>
                    )}
                    <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="cc-name">Nombre *</Label>
                        <Input
                            id="cc-name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Feria de Pesca Vigo 2026"
                        />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="cc-desc">Descripción</Label>
                        <Textarea
                            id="cc-desc"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Cupones en el stand…"
                            rows={2}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="cc-period">Duración del PRO *</Label>
                        <select
                            id="cc-period"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            value={period}
                            onChange={e => setPeriod(e.target.value as PeriodValue)}
                        >
                            {PERIOD_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="cc-max">
                            {codeGenMode === 'unique_codes' ? 'Cantidad de códigos *' : 'Máximo de usuarios *'}
                        </Label>
                        <Input
                            id="cc-max"
                            type="number"
                            min={1}
                            max={10000}
                            value={maxRedemptions}
                            onChange={e => setMaxRedemptions(parseInt(e.target.value || '0', 10))}
                        />
                        <p className="text-xs text-muted-foreground">
                            {codeGenMode === 'unique_codes'
                                ? 'Entre 1 y 10000 códigos distintos.'
                                : 'Entre 1 y 10000 usuarios distintos podrán usar el mismo código.'}
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="cc-valid">Caducidad de la campaña</Label>
                        <Input
                            id="cc-valid"
                            type="date"
                            value={validUntil}
                            onChange={e => setValidUntil(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Vacío = sin caducidad.</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="cc-channel">Canal de origen</Label>
                        <select
                            id="cc-channel"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            value={sourceChannel || ''}
                            onChange={e => setSourceChannel(e.target.value)}
                        >
                            <option value="">— Ninguno —</option>
                            <option value="event">Evento físico</option>
                            <option value="partner">Partner / colaboración</option>
                            <option value="press">Prensa</option>
                            <option value="influencer">Influencer</option>
                            <option value="internal">Interno / equipo</option>
                            <option value="other">Otro</option>
                        </select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="cc-label">Etiqueta del origen</Label>
                        <Input
                            id="cc-label"
                            value={sourceLabel}
                            onChange={e => setSourceLabel(e.target.value)}
                            placeholder="Stand AiPeces · Vigo"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="cc-utm">UTM campaign</Label>
                        <Input
                            id="cc-utm"
                            value={utmCampaign}
                            onChange={e => setUtmCampaign(e.target.value)}
                            placeholder="vigo2026_evento"
                        />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="cc-notes">Notas internas</Label>
                        <Textarea
                            id="cc-notes"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={2}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={() => createMutation.mutate()} disabled={!valid || createMutation.isPending}>
                        {createMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Plus className="w-4 h-4" />
                        )}
                        Crear campaña
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CampaignDetailDialog({
    campaign,
    onClose,
}: {
    campaign: CampaignRow | null;
    onClose: () => void;
}) {
    const [tab, setTab] = useState<'codes' | 'redemptions'>('codes');
    const [onlyUnredeemed, setOnlyUnredeemed] = useState(false);

    useEffect(() => {
        setTab('codes');
        setOnlyUnredeemed(false);
    }, [campaign?.id]);

    const { data: codes, isLoading } = useQuery({
        queryKey: ['admin-panel', 'pro-coupons', 'codes', campaign?.id, onlyUnredeemed],
        queryFn: async () => {
            if (!campaign) return [];
            const { data, error: rpcErr } = await dbClient.rpc('admin_get_campaign_codes', {
                p_campaign_id: campaign.id,
                p_only_unredeemed: onlyUnredeemed,
            });
            if (rpcErr) throw new Error(rpcErr.message);
            return normalizeCodes(data);
        },
        enabled: !!campaign,
    });

    const { data: grantedRedemptions, isLoading: redemptionsLoading } = useQuery({
        queryKey: ['admin-panel', 'pro-coupons', 'redemptions', campaign?.id],
        queryFn: async () => {
            if (!campaign) return [];
            const { data, error: rpcErr } = await dbClient.rpc('admin_get_campaign_redemptions', {
                p_campaign_id: campaign.id,
            });
            if (rpcErr) throw new Error(rpcErr.message);
            return normalizeRedemptions(data);
        },
        enabled: !!campaign,
    });

    if (!campaign) return null;

    const redemptions = grantedRedemptions ?? [];

    const exportCsv = () => {
        const rows: string[][] = [
            ['code', 'redeemed_username', 'redeemed_email', 'redeemed_at'],
            ...(codes ?? []).map(c => [
                c.code,
                c.redeemedUsername ?? '',
                c.redeemedEmail ?? '',
                c.redeemedAt ?? '',
            ]),
        ];
        const safeName = campaign.name.replace(/[^a-z0-9-_]+/gi, '_');
        downloadCsv(`cupones_${safeName}.csv`, rows);
    };

    return (
        <Dialog open={!!campaign} onOpenChange={o => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Ticket className="w-5 h-5 text-primary" />
                        {campaign.name}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground text-left">
                        {PERIOD_LABEL[campaign.period] ?? campaign.period} · {CODE_GEN_LABEL[campaign.codeGenerationMode]} ·{' '}
                        {campaign.grantedCount} de {campaign.maxRedemptions} canjeados
                    </p>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
                    <Button
                        variant={tab === 'codes' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTab('codes')}
                    >
                        Códigos ({codes?.length ?? 0})
                    </Button>
                    <Button
                        variant={tab === 'redemptions' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTab('redemptions')}
                    >
                        Canjes ({grantedRedemptions?.length ?? 0})
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        {tab === 'codes' && campaign.codeGenerationMode === 'unique_codes' && (
                            <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={onlyUnredeemed}
                                    onChange={e => setOnlyUnredeemed(e.target.checked)}
                                />
                                Solo sin canjear
                            </label>
                        )}
                        <Button variant="outline" size="sm" onClick={exportCsv}>
                            <Download className="w-4 h-4" />
                            CSV
                        </Button>
                    </div>
                </div>

                {isLoading || (tab === 'redemptions' && redemptionsLoading) ? (
                    <div className="p-8 text-center text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                ) : tab === 'codes' ? (
                    <div className="rounded-lg border border-border overflow-hidden max-h-[50vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-card border-b border-border">
                                <tr>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Código
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Estado
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Canjeado por
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Fecha
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {(codes ?? []).map(c => (
                                    <tr key={c.code}>
                                        <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                                        <td className="px-3 py-2">
                                            {campaign.codeGenerationMode === 'shared_code' ? (
                                                <span className="text-xs text-muted-foreground" title="El cupo se ve en la cabecera">
                                                    Código compartido
                                                </span>
                                            ) : c.redeemedBy ? (
                                                <span className="text-xs font-medium text-emerald-400">
                                                    Canjeado
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">
                                                    Disponible
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {c.redeemedUsername ? (
                                                <div>
                                                    <div className="font-medium text-sm">
                                                        {c.redeemedUsername}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {c.redeemedEmail}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {c.redeemedAt
                                                ? format(new Date(c.redeemedAt), 'dd MMM yyyy HH:mm', {
                                                      locale: es,
                                                  })
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                                {(codes ?? []).length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-3 py-6 text-center text-muted-foreground"
                                        >
                                            Sin códigos.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="rounded-lg border border-border overflow-hidden max-h-[50vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-card border-b border-border">
                                <tr>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Usuario
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Email
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Código
                                    </th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                                        Fecha
                                    </th>
                                </tr>
                            </thead>
                        <tbody className="divide-y divide-border">
                            {redemptions.map(r => (
                                <tr key={r.id}>
                                    <td className="px-3 py-2 font-medium text-sm">
                                        {r.username ?? '—'}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                        {r.email ?? '—'}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.codeText}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {r.redeemedAt
                                            ? format(new Date(r.redeemedAt), 'dd MMM yyyy HH:mm', {
                                                  locale: es,
                                              })
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                                {redemptions.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-3 py-6 text-center text-muted-foreground"
                                        >
                                            Aún sin canjes.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
